import { nanoid } from 'nanoid';
import { create } from 'zustand';
import { useShallow } from 'zustand/react/shallow';

import { setCloudStorageValue } from '@/shared/helpers/cloud-storage';
import { resetAllStores } from '@/shared/helpers/create-clearable-store';
import {
  isValidLocale,
  Languages,
  LS_FORCE_LOCALE_KEY,
} from '@/shared/services/localization';

import {
  type AppState,
  type DefaultAppState,
  PageWrapperStyle,
  type ParsedToken,
  SingletonName,
  ThemeMode,
  UIKit,
} from './types';

const defaultAppState: DefaultAppState = {
  parsedToken: {} as ParsedToken,
  theme: ThemeMode.DARK,
  token: '',
  lang: Languages.en,
  uiKit: UIKit.MATERIAL,
  pageWrapperStyle: PageWrapperStyle.PRIMARY,
  marketSessionId: nanoid(),
  wsSentInitialMsg: false,
  i18n: undefined,
  singletons: {
    [SingletonName.BACK_BUTTON]: false,
  },
};

export const useAppStateRaw = create<AppState>()((set) => ({
  ...defaultAppState,
  actions: {
    hydrate: (fresh: Partial<AppState>) => set(fresh),
    setTheme: (theme: ThemeMode) => {
      document.documentElement.setAttribute('data-theme', theme);
      set({ theme });
    },
    setLanguage: (lang) => {
      if (isValidLocale(lang)) {
        set({ lang });
        void setCloudStorageValue(LS_FORCE_LOCALE_KEY, lang);

        resetAllStores();
      }
    },
    setPageWrapperStyle: (cls: PageWrapperStyle) => set({ pageWrapperStyle: cls }),
    reset: () => set((prev: AppState) => ({ ...prev, ...defaultAppState })),
    updateSingletons: (args: Partial<Record<SingletonName, boolean>>) =>
      set((prev) => ({
        singletons: { ...prev.singletons, ...args },
      })),
  },
}));

export const useAppState = () =>
  useAppStateRaw(
    useShallow((state) => ({
      ...state,
    }))
  );

export const useAppActions = () => useAppStateRaw(useShallow((state) => state.actions));

export const useToken = () => useAppStateRaw(useShallow((state) => state.token));
export const useParsedToken = () =>
  useAppStateRaw(useShallow((state) => state.parsedToken));

export const useAppLang = () => useAppStateRaw(useShallow((state) => state.lang));
export const useUIKit = () => useAppStateRaw(useShallow((state) => state.uiKit));
export const useTheme = () => useAppStateRaw(useShallow((state) => state.theme));
export const useSingletons = () =>
  useAppStateRaw(useShallow((state) => state.singletons));
